// src/components/CategoryModal.tsx
import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  createCategory,
  updateCategory,
  selectCategoriesLoading,
  selectCategoriesError,
  clearError
} from 'store/categoriesSlice';
import { getCompanies, Company } from 'api/companyAPI';
import { createBulkCategoryAllCompanies } from 'api/assetsAPI';
import { getEffectiveCompanyContext } from 'api/contextAPI';

interface CategoryModalProps {
  show: boolean;
  handleClose: () => void;
  editingCategory?: {
    id: number;
    name: string;
    company_id: number;
  } | null;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      detail?: string | Array<{ msg?: string; message?: string }>;
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

const CategoryModal = ({
  show,
  handleClose,
  editingCategory = null
}: CategoryModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectCategoriesLoading);
  const error = useSelector(selectCategoriesError);
  const { companyId, role } = useSelector((state: RootState) => state.auth);

  const [formError, setFormError] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const userCompanyId = companyId ? parseInt(companyId) : null;
  const isSuperAdmin = role === 'super_user';

  // State for effective company ID from context API
  const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company_id: isSuperAdmin ? 0 : userCompanyId || 0
  });

  // Checkbox state for bulk creation
  const [createForAllCompanies, setCreateForAllCompanies] = useState(false);

  // Fetch effective company from context API
  useEffect(() => {
    const fetchEffectiveCompany = async () => {
      try {
        const context = await getEffectiveCompanyContext();
        setEffectiveCompanyId(context.company_id || null);
      } catch (error) {
        console.error('Failed to fetch effective company:', error);
        // Fallback to userCompanyId if context API fails
        setEffectiveCompanyId(userCompanyId);
      }
    };

    if (show) {
      fetchEffectiveCompany();
      if (isSuperAdmin && !editingCategory) {
        loadCompanies();
      }
      resetForm();
    }
  }, [show, isSuperAdmin, editingCategory, userCompanyId]);

  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name,
        company_id: editingCategory.company_id
      });
    }
  }, [editingCategory]);

  useEffect(() => {
    if (error) {
      setFormError(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const loadCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const resetForm = () => {
    // Use effectiveCompanyId from context API, fallback to userCompanyId
    const defaultCompanyId = isSuperAdmin
      ? 0
      : effectiveCompanyId || userCompanyId || 0;

    setFormData({
      name: '',
      company_id: defaultCompanyId
    });
    setFormError('');
    setCreateForAllCompanies(false);
  };

  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validation
    if (!formData.name.trim()) {
      setFormError('Category name is required');
      return;
    }

    if (
      !editingCategory &&
      !createForAllCompanies &&
      (!formData.company_id || formData.company_id === 0)
    ) {
      setFormError('Company is required or select "Create for All Companies"');
      return;
    }

    try {
      if (editingCategory) {
        // Update category
        await dispatch(
          updateCategory({
            categoryId: editingCategory.id,
            categoryData: {
              name: formData.name.trim(),
              description: ''
            }
          })
        ).unwrap();
      } else if (createForAllCompanies) {
        // Create category for all companies
        await createBulkCategoryAllCompanies({
          name: formData.name.trim(),
          description: ''
        });
        // Refresh categories list
        window.location.reload();
      } else {
        // Create category for specific company
        await dispatch(
          createCategory({
            name: formData.name.trim(),
            description: '',
            company_id: formData.company_id
          })
        ).unwrap();
      }

      handleCloseModal();
    } catch (error: unknown) {
      console.error('Failed to save category:', error);
      const apiError = error as ApiError;

      let errorMessage = `Failed to ${
        editingCategory ? 'update' : 'create'
      } category`;

      // ✅ PROPERLY EXTRACT BACKEND ERROR MESSAGE
      if (apiError.response?.data?.detail) {
        // Handle string or array detail
        if (typeof apiError.response.data.detail === 'string') {
          errorMessage = apiError.response.data.detail;
        } else if (Array.isArray(apiError.response.data.detail)) {
          // Handle validation error array
          const firstError = apiError.response.data.detail[0];
          errorMessage = firstError?.msg || firstError?.message || errorMessage;
        }
      } else if (apiError.response?.data?.message) {
        errorMessage = apiError.response.data.message;
      } else if (apiError.response?.data?.error) {
        errorMessage = apiError.response.data.error;
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setFormError(errorMessage);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'company_id' ? Number(value) : value
    }));

    if (formError) setFormError('');
  };

  return (
    <Modal show={show} onHide={handleCloseModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingCategory ? 'Edit Category' : 'Create New Category'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            {/* Company Selector - Only for Super Admin and only when creating (not editing) */}
            {isSuperAdmin && !editingCategory && (
              <>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Company *</Form.Label>
                    <Form.Select
                      name="company_id"
                      value={formData.company_id}
                      onChange={handleInputChange}
                      required={!createForAllCompanies}
                      disabled={createForAllCompanies}
                    >
                      <option value={0}>Select Company *</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="createForAllCompanies"
                      label="Create this category for all companies"
                      checked={createForAllCompanies}
                      onChange={e => setCreateForAllCompanies(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      When checked, this category will be created for all
                      existing companies at once
                    </Form.Text>
                  </Form.Group>
                </Col>
              </>
            )}

            {/* Category Name Field - Always shown */}
            <Col md={12}>
              <Form.Group>
                <Form.Label>Category Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter category name"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading
              ? 'Saving...'
              : editingCategory
              ? 'Update Category'
              : 'Create Category'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CategoryModal;
